// SEQ-180 legibility follow-up (15 September 2026): what "this chart has
// nothing to draw" actually means.
//
// THE DEFECT THIS FIXES. Two of the four phase 1 charts tested the wrong
// thing. Both asked whether they had been handed any ROWS, and with "This
// month" selected the answer is yes: every project is still a row, it just has
// no figure for the month. So the bar chart drew five project names down an
// axis with no bars, no X axis and no message, and the line chart drew an empty
// box with a legend under it. Both read as a rendering failure rather than as
// an absence of data, which is the worse of the two because it makes the reader
// distrust the numbers that ARE there.
//
// A chart is empty when nothing in it can be plotted, not when nothing was
// fetched. That is one predicate, and it is the same one for every chart.

/**
 * A row of anything, read by the field names a chart plots.
 *
 * Declared as a lookup rather than as `Record<string, unknown>` on purpose: an
 * interface without an index signature is not assignable to a Record, and every
 * row on this page is an interface (ProjectRollup, MonthRollup). Reading the
 * field through an indexed access keeps the call sites free of casts.
 */
type PlottableRow = object;

function isPlottable(value: unknown): boolean {
  return typeof value === "number" && Number.isFinite(value);
}

function field(row: PlottableRow, key: string): unknown {
  return (row as Record<string, unknown>)[key];
}

/**
 * Whether ONE row carries a real number for every field named.
 *
 * Exported because a scatter needs the same rule twice: once to decide whether
 * it has anything to draw, and once to choose which rows it draws. Two spellings
 * of "has a value" is how a chart ends up deciding it is empty on one rule and
 * plotting on another.
 */
export function isPlottableRow(row: PlottableRow, keys: readonly string[]): boolean {
  if (keys.length === 0) return false;
  return keys.every((key) => isPlottable(field(row, key)));
}

/**
 * How many rows carry a real number for EVERY field named.
 *
 * The count a scatter needs: a point with an X and no Y cannot be drawn
 * anywhere, so it is not a point.
 */
export function countPlottable(rows: readonly PlottableRow[], keys: readonly string[]): number {
  return rows.filter((row) => isPlottableRow(row, keys)).length;
}

/**
 * Whether ANY row carries a real number for ANY field named.
 *
 * The test a multi-series line or bar chart needs: one series with data is a
 * chart worth drawing, and the other series' gaps are the reader's information
 * rather than a fault.
 */
export function hasPlottableValues(rows: readonly PlottableRow[], keys: readonly string[]): boolean {
  return rows.some((row) => keys.some((key) => isPlottable(field(row, key))));
}

/**
 * The words an empty chart says.
 *
 * Two sentences, because "no data" alone leaves the reader wondering whether
 * the page is broken. The first says what is true, the second says what to do
 * about it, in the same shape as WIDGET_EMPTY_MESSAGE
 * (PnlWidgetGenericCharts.tsx) so the built widgets and the phase 1 ones do not
 * say the same thing two ways.
 */
export const CHART_EMPTY_TITLE = "Nothing to plot for this range";

export const CHART_EMPTY_HINT = "The projects are still here, they just have no figures for these dates. Try a wider date range, or clear a project filter.";
